import type { ApolloError } from '@apollo/client'
import { useMutation } from '@apollo/client'
import {
  Alert,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Separator
} from '@chain-zen/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import type { ControllerRenderProps } from 'react-hook-form'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import * as z from 'zod'

import {
  DISABLE_APP,
  ENABLE_EXPEDITE_ALL_BOOKING_APP,
  ENABLE_EXPEDITE_ALL_TRACKING_APP
} from '../../../data/mutations/apps'
import { GET_APPS } from '../../../data/queries/apps'
import { useAuth } from '../../../hooks/use-auth'
import { AppTypeEnum } from '../../../types/workspace'
import type { AppModalProps } from '../apps-data'

const bookingFormSchema = z.object({
  clientID: z.string().min(1, 'Client ID is required'),
  clientSecret: z.string().min(1, 'Client Secret is required')
})

const trackingFormSchema = z.object({
  authUsername: z.string().min(1, 'Auth Username is required'),
  authPassword: z.string().min(1, 'Auth Password is required'),
  shipmentID: z.string().min(1, 'Shipment ID is required'),
  customerID: z.string().min(1, 'Customer ID is required')
})

type EnableExpediteAllBookingAppInput = {
  appType: AppTypeEnum
  clientID: string
  clientSecret: string
}

type EnableExpediteAllTrackingAppInput = {
  appType: AppTypeEnum
  authUsername: string
  authPassword: string
  shipmentID: string
  customerID: string
}

/**
 * Extracts the error message from ApolloError, checking graphQLErrors first, then falling back to the main message
 */
const getErrorMessage = (err: ApolloError): string => {
  // Check if there are GraphQL errors with specific messages
  if (err.graphQLErrors && err.graphQLErrors.length > 0) {
    const graphQLError = err.graphQLErrors[0]
    // Check if error message exists in GraphQL error
    if (graphQLError.message) {
      return graphQLError.message
    }
    // Check if error message is in extensions
    const extensions = graphQLError.extensions as { response?: { body?: { message?: string } } } | undefined
    if (extensions?.response?.body?.message) {
      return extensions.response.body.message
    }
  }
  // Fall back to the main error message
  return err.message || 'Unknown error occurred'
}

/**
 * This is the modal for the Expedite All app.
 * It displays two side-by-side sections for Booking and Tracking, each with their own Enable/Disable buttons.
 */
export function ExpediteAllModal({
  isOpen,
  onClose,
  bookingWorkspaceApp,
  trackingWorkspaceApp,
  isModalEditable,
  setModalEditable
}: AppModalProps) {
  const { selectedWorkspace } = useAuth()
  const workspaceID = selectedWorkspace?.workspace.id || ''
  const [bookingFormError, setBookingFormError] = useState('')
  const [trackingFormError, setTrackingFormError] = useState('')

  const isBookingEnabled = bookingWorkspaceApp?.enabled ?? false
  const isTrackingEnabled = trackingWorkspaceApp?.enabled ?? false

  const bookingForm = useForm<z.infer<typeof bookingFormSchema>>({
    defaultValues: {
      clientID: '',
      clientSecret: ''
    },
    resolver: zodResolver(bookingFormSchema)
  })

  const trackingForm = useForm<z.infer<typeof trackingFormSchema>>({
    defaultValues: {
      authUsername: '',
      authPassword: '',
      shipmentID: '',
      customerID: ''
    },
    resolver: zodResolver(trackingFormSchema)
  })

  // Pre-fill forms when editing and modal is open
  useEffect(() => {
    if (!isOpen) {
      // Reset forms when modal is closed
      bookingForm.reset({
        clientID: '',
        clientSecret: ''
      })
      trackingForm.reset({
        authUsername: '',
        authPassword: '',
        shipmentID: '',
        customerID: ''
      })
      return
    }

    if (isModalEditable && isOpen) {
      // When updating, clear all fields - user must re-enter all credentials
      bookingForm.reset({
        clientID: '',
        clientSecret: ''
      })
      trackingForm.reset({
        authUsername: '',
        authPassword: '',
        shipmentID: '',
        customerID: ''
      })
    } else if (!isModalEditable && isOpen) {
      // Reset forms when modal opens in enable mode (not editing)
      bookingForm.reset({
        clientID: '',
        clientSecret: ''
      })
      trackingForm.reset({
        authUsername: '',
        authPassword: '',
        shipmentID: '',
        customerID: ''
      })
    }
  }, [
    isOpen,
    isModalEditable,
    isBookingEnabled,
    isTrackingEnabled,
    bookingWorkspaceApp,
    trackingWorkspaceApp,
    bookingForm,
    trackingForm
  ])

  const [enableBookingApp, { loading: enableBookingLoading }] = useMutation<
    { enableExpediteAllBookingApp: { appEnabled: boolean } },
    EnableExpediteAllBookingAppInput
  >(ENABLE_EXPEDITE_ALL_BOOKING_APP, {
    context: {
      headers: {
        'ws-id': workspaceID
      }
    },
    onCompleted: () => {
      toast.success(
        isModalEditable
          ? 'Expedite All Booking app updated successfully!'
          : 'Expedite All Booking app enabled successfully!'
      )
      setBookingFormError('')
      if (isModalEditable && setModalEditable) {
        setModalEditable(false)
      }
      if (!isModalEditable) {
        bookingForm.reset()
      }
    },
    onError: (err: ApolloError) => {
      const backendErrorMessage = getErrorMessage(err)
      const errorMessage =
        backendErrorMessage && backendErrorMessage !== 'Unknown error occurred'
          ? backendErrorMessage
          : `Something went wrong. Please contact support@chaineapp.com. Error: ${backendErrorMessage}`
      setBookingFormError(errorMessage)
      toast.error(errorMessage)
    },
    refetchQueries: [GET_APPS]
  })

  const [enableTrackingApp, { loading: enableTrackingLoading }] = useMutation<
    { enableExpediteAllTrackingApp: { appEnabled: boolean } },
    EnableExpediteAllTrackingAppInput
  >(ENABLE_EXPEDITE_ALL_TRACKING_APP, {
    context: {
      headers: {
        'ws-id': workspaceID
      }
    },
    onCompleted: () => {
      toast.success(
        isModalEditable
          ? 'Expedite All Tracking app updated successfully!'
          : 'Expedite All Tracking app enabled successfully!'
      )
      setTrackingFormError('')
      if (isModalEditable && setModalEditable) {
        setModalEditable(false)
      }
      if (!isModalEditable) {
        trackingForm.reset()
      }
    },
    onError: (err: ApolloError) => {
      const backendErrorMessage = getErrorMessage(err)
      const errorMessage =
        backendErrorMessage && backendErrorMessage !== 'Unknown error occurred'
          ? backendErrorMessage
          : `Something went wrong. Please contact support@chaineapp.com. Error: ${backendErrorMessage}`
      setTrackingFormError(errorMessage)
      toast.error(errorMessage)
    },
    refetchQueries: [GET_APPS]
  })

  const [disableBookingApp, { loading: disableBookingLoading }] = useMutation(DISABLE_APP, {
    context: {
      headers: {
        'ws-id': workspaceID
      }
    },
    onCompleted: () => {
      toast.success('Expedite All Booking app disabled successfully!')
      setBookingFormError('')
    },
    onError: (err: ApolloError) => {
      const backendErrorMessage = getErrorMessage(err)
      const errorMessage =
        backendErrorMessage && backendErrorMessage !== 'Unknown error occurred'
          ? backendErrorMessage
          : `Something went wrong. Please contact support@chaineapp.com. Error: ${backendErrorMessage}`
      setBookingFormError(errorMessage)
      toast.error(errorMessage)
    },
    refetchQueries: [GET_APPS]
  })

  const [disableTrackingApp, { loading: disableTrackingLoading }] = useMutation(DISABLE_APP, {
    context: {
      headers: {
        'ws-id': workspaceID
      }
    },
    onCompleted: () => {
      toast.success('Expedite All Tracking app disabled successfully!')
      setTrackingFormError('')
    },
    onError: (err: ApolloError) => {
      const backendErrorMessage = getErrorMessage(err)
      const errorMessage =
        backendErrorMessage && backendErrorMessage !== 'Unknown error occurred'
          ? backendErrorMessage
          : `Something went wrong. Please contact support@chaineapp.com. Error: ${backendErrorMessage}`
      setTrackingFormError(errorMessage)
      toast.error(errorMessage)
    },
    refetchQueries: [GET_APPS]
  })

  const handleClose = () => {
    setBookingFormError('')
    setTrackingFormError('')
    bookingForm.reset()
    trackingForm.reset()
    if (isModalEditable && setModalEditable) {
      setModalEditable(false)
    }
    onClose()
  }

  const onBookingSubmit = (values: z.infer<typeof bookingFormSchema>) => {
    enableBookingApp({
      variables: {
        appType: AppTypeEnum.ExpediteAllBooking,
        clientID: values.clientID,
        clientSecret: values.clientSecret
      }
    })
  }

  const onTrackingSubmit = (values: z.infer<typeof trackingFormSchema>) => {
    enableTrackingApp({
      variables: {
        appType: AppTypeEnum.ExpediteAllTracking,
        authUsername: values.authUsername,
        authPassword: values.authPassword,
        shipmentID: values.shipmentID,
        customerID: values.customerID
      }
    })
  }

  const handleDisableBooking = () => {
    disableBookingApp({
      context: {
        headers: {
          'ws-id': bookingWorkspaceApp?.workspaceID || ''
        }
      },
      variables: { appType: AppTypeEnum.ExpediteAllBooking }
    })
  }

  const handleDisableTracking = () => {
    disableTrackingApp({
      context: {
        headers: {
          'ws-id': trackingWorkspaceApp?.workspaceID || ''
        }
      },
      variables: { appType: AppTypeEnum.ExpediteAllTracking }
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-6xl w-full max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isModalEditable ? 'Update' : 'Enable'} Expedite All for Chain</DialogTitle>
          <DialogDescription>
            {isModalEditable
              ? 'Update Expedite All Booking and Tracking integrations. Each can be updated or disabled independently.'
              : 'Enable Expedite All Booking and Tracking integrations. Each can be enabled or disabled independently.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-row gap-6 mt-4 min-w-0">
          {/* Tracking Section */}
          <div className="flex-1 space-y-4 min-w-0">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Tracking</h3>
              {!!isTrackingEnabled && <Badge className="bg-green-500 text-white border-green-600">Enabled</Badge>}
            </div>

            <Form {...trackingForm}>
              <div className="space-y-4">
                <FormField
                  control={trackingForm.control}
                  name="authUsername"
                  render={({
                    field
                  }: {
                    field: ControllerRenderProps<z.infer<typeof trackingFormSchema>, 'authUsername'>
                  }) => (
                    <FormItem>
                      <FormLabel>Auth Username</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter Auth Username"
                          {...field}
                          disabled={
                            enableTrackingLoading || disableTrackingLoading || (isTrackingEnabled && !isModalEditable)
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={trackingForm.control}
                  name="authPassword"
                  render={({
                    field
                  }: {
                    field: ControllerRenderProps<z.infer<typeof trackingFormSchema>, 'authPassword'>
                  }) => (
                    <FormItem>
                      <FormLabel>Auth Password</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="Enter Auth Password"
                          {...field}
                          disabled={
                            enableTrackingLoading || disableTrackingLoading || (isTrackingEnabled && !isModalEditable)
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={trackingForm.control}
                  name="shipmentID"
                  render={({
                    field
                  }: {
                    field: ControllerRenderProps<z.infer<typeof trackingFormSchema>, 'shipmentID'>
                  }) => (
                    <FormItem>
                      <FormLabel>Shipment ID</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter Shipment ID"
                          {...field}
                          disabled={
                            enableTrackingLoading || disableTrackingLoading || (isTrackingEnabled && !isModalEditable)
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={trackingForm.control}
                  name="customerID"
                  render={({
                    field
                  }: {
                    field: ControllerRenderProps<z.infer<typeof trackingFormSchema>, 'customerID'>
                  }) => (
                    <FormItem>
                      <FormLabel>Customer ID</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter Customer ID"
                          {...field}
                          disabled={
                            enableTrackingLoading || disableTrackingLoading || (isTrackingEnabled && !isModalEditable)
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {!!trackingFormError && <Alert variant="error" description={trackingFormError} />}

                <div className="flex justify-end gap-2">
                  {isTrackingEnabled && !isModalEditable ? (
                    <Button variant="destructive" onClick={handleDisableTracking} disabled={disableTrackingLoading}>
                      {disableTrackingLoading ? 'Disabling...' : 'Disable'}
                    </Button>
                  ) : (
                    <Button onClick={trackingForm.handleSubmit(onTrackingSubmit)} disabled={enableTrackingLoading}>
                      {enableTrackingLoading
                        ? isModalEditable
                          ? 'Updating...'
                          : 'Enabling...'
                        : isModalEditable
                          ? 'Update'
                          : 'Enable'}
                    </Button>
                  )}
                </div>
              </div>
            </Form>
          </div>

          <Separator orientation="vertical" className="flex-shrink-0" />

          {/* Booking Section */}
          <div className="flex-1 space-y-4 min-w-0">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Booking</h3>
              {!!isBookingEnabled && <Badge className="bg-green-500 text-white border-green-600">Enabled</Badge>}
            </div>

            <Form {...bookingForm}>
              <div className="space-y-4">
                <FormField
                  control={bookingForm.control}
                  name="clientID"
                  render={({
                    field
                  }: {
                    field: ControllerRenderProps<z.infer<typeof bookingFormSchema>, 'clientID'>
                  }) => (
                    <FormItem>
                      <FormLabel>Client ID</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter Client ID"
                          {...field}
                          disabled={
                            enableBookingLoading || disableBookingLoading || (isBookingEnabled && !isModalEditable)
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={bookingForm.control}
                  name="clientSecret"
                  render={({
                    field
                  }: {
                    field: ControllerRenderProps<z.infer<typeof bookingFormSchema>, 'clientSecret'>
                  }) => (
                    <FormItem>
                      <FormLabel>Client Secret</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="Enter Client Secret"
                          {...field}
                          disabled={
                            enableBookingLoading || disableBookingLoading || (isBookingEnabled && !isModalEditable)
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {!!bookingFormError && <Alert variant="error" description={bookingFormError} />}

                <div className="flex justify-end gap-2">
                  {isBookingEnabled && !isModalEditable ? (
                    <Button variant="destructive" onClick={handleDisableBooking} disabled={disableBookingLoading}>
                      {disableBookingLoading ? 'Disabling...' : 'Disable'}
                    </Button>
                  ) : (
                    <Button onClick={bookingForm.handleSubmit(onBookingSubmit)} disabled={enableBookingLoading}>
                      {enableBookingLoading
                        ? isModalEditable
                          ? 'Updating...'
                          : 'Enabling...'
                        : isModalEditable
                          ? 'Update'
                          : 'Enable'}
                    </Button>
                  )}
                </div>
              </div>
            </Form>
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={handleClose} type="button">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
